import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Stack, Button, TextField, MenuItem, TableCell, Backdrop, CircularProgress, Chip, InputAdornment, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Search } from '@mui/icons-material';
import Header from '../Components/layout/Header';
import SorTable from '../Components/ui/sortable';
import { API } from '../conf';
import { Context } from '../App';
import moment from 'moment';

const TYPE_LABELS = {
    creation:     'Création',
    modification: 'Modification',
    suppression:  'Suppression',
    cloture:      'Clôture',
    connexion:    'Connexion',
    autre:        'Autre',
}

const TYPE_COLORS = {
    creation:     'success',
    modification: 'primary',
    suppression:  'error',
    cloture:      'warning',
    connexion:    'default',
    autre:        'secondary',
}

function getType(action) {
    if (!action) return 'autre'
    if (/connexion/i.test(action)) return 'connexion'
    if (/enregistrement|création|ajout/i.test(action)) return 'creation'
    if (/suppression|annulation|réinitialisation.*(église)/i.test(action)) return 'suppression'
    if (/clôture/i.test(action)) return 'cloture'
    if (/modification|update|correction|transfert|réception|intégration|complétion|mise à jour|contrôle|validation|signal|recalcul|réinitialisation/i.test(action)) return 'modification'
    return 'autre'
}

export default function LogManager() {
    const { updateConnect } = useContext(Context)
    const [start_date, setStart] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'))
    const [end_date, setEnd] = useState(moment().format('YYYY-MM-DD'))
    const [type, setType] = useState([])
    const [egliseNum, setEgliseNum] = useState('')
    const [userNum, setUserNum] = useState('')
    const [roleFilter, setRoleFilter] = useState('')
    const [actionFilter, setActionFilter] = useState('')
    const [search_input, setSearch] = useState('')
    const [logs, setLogs] = useState(null)
    const [eglises, setEglises] = useState([])
    const [users, setUsers] = useState([])
    const [actions, setActions] = useState([])
    const [loading, setLoad] = useState(false)

    const table = [
        {header: 'Date', sortable: true, sortBy: el => new Date(el.when).getTime(), row: (row, i) =>
            <TableCell key={i} sx={{minWidth: '140px'}}>{moment(row.when).format('DD/MM/YYYY HH:mm')}</TableCell>},
        {header: 'Utilisateur', sortable: true, sortBy: el => (el.who?.nom || '').toLowerCase(), row: (row, i) =>
            <TableCell key={i} sx={{minWidth: '140px', textTransform: 'capitalize'}}>{[row.who?.nom, row.who?.prenom1, row.who?.prenom2].filter(Boolean).join(' ')}</TableCell>},
        {header: 'Organisation', sortable: true, sortBy: el => (el.who?.organisation || '').toLowerCase(), row: (row, i) =>
            <TableCell key={i} sx={{minWidth: '150px'}}>{row.who?.organisation}</TableCell>},
        {header: 'Type', sortable: true, sortBy: el => getType(el.action) || '', row: (row, i) => {
            const t = getType(row.action)
            return <TableCell key={i}>{t && <Chip label={TYPE_LABELS[t]} color={TYPE_COLORS[t]} size='small'/>}</TableCell>
        }},
        {header: 'Action', sortable: true, sortBy: el => (el.action || '').toLowerCase(), row: (row, i) =>
            <TableCell key={i} sx={{minWidth: '200px', width: '100%'}} align='center'>{row.action}</TableCell>},
        {header: 'Référence', sortable: true, sortBy: el => (el.whatKey || '').toLowerCase(), row: (row, i) =>
            <TableCell key={i} sx={{minWidth: '140px', fontSize: '12px', color: 'text.secondary'}}>{row.whatKey}</TableCell>},
    ]

    function normalize(str) {
        return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9\s]/gi, '').toLowerCase()
    }

    function get_searchable(row) {
        return [row.who?.nom, row.who?.prenom1, row.who?.organisation, row.action, row.what, row.whatKey, row.where].filter(Boolean).join(' ')
    }

    function fetchLogs() {
        if (!start_date || !end_date) return
        setLoad(true)
        fetch(API.concat('/logs/search'), {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
            body: JSON.stringify({
                start: start_date,
                end: end_date,
                eglise: egliseNum || undefined,
                userNum: userNum || undefined,
                action: actionFilter || undefined,
                type: type.length === 1 ? type[0] : undefined,
            })
        }).then(async res => {
            const token = res.headers.get('x-auth-token')
            if (res.status === 401) { sessionStorage.clear(); updateConnect(false) }
            else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
                if (res.status === 200) setLogs(await res.json())
            }
            setLoad(false)
        })
    }

    useEffect(() => {
        const headers = { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }

        fetch(API.concat('/logs/distinct/actions'), { method: 'GET', headers })
            .then(async r => { if (r.status === 200) setActions(await r.json()) })

        fetch(API.concat('/logs/distinct/users'), { method: 'GET', headers })
            .then(async r => { if (r.status === 200) setUsers(await r.json()) })

        fetch(API.concat(`/eglises/by/${JSON.stringify({type: {$in: ['église', 'fédération']}})}`), { method: 'GET', headers })
            .then(async r => { if (r.status === 200) setEglises(await r.json()) })
    }, [])

    const roleUsers = roleFilter ? new Set(users.filter(u => u.role === roleFilter).map(u => u.num)) : null

    const filtered = logs ? logs.filter(row => {
        if (type.length > 0) { const t = getType(row.action); if (!type.includes(t)) return false }
        if (roleUsers && !roleUsers.has(row.who?.num)) return false
        return normalize(get_searchable(row)).includes(normalize(search_input))
    }) : []

    const distinctRoles = [...new Set(users.map(u => u.role).filter(Boolean))].sort()

    return (
        <div style={{display: 'flex', flexDirection: 'column', paddingBottom: '30px'}}>
            <Header/>
            <Typography sx={{textAlign: 'center', marginTop: '20px', fontSize: 26}}>Journaux de logs</Typography>

            <Box sx={{paddingX: '20px', marginTop: '20px'}}>
                <Stack direction='row' spacing={2} flexWrap='wrap' alignItems='center' mb={2}>
                    <TextField size='small' type='date' label='Début' InputLabelProps={{shrink: true}} value={start_date}
                        onChange={e => { setStart(e.target.value); if (end_date && new Date(end_date) < new Date(e.target.value)) setEnd('') }} />
                    <TextField size='small' type='date' label='Fin' InputLabelProps={{shrink: true}} value={end_date}
                        inputProps={{min: start_date}}
                        onChange={e => setEnd(e.target.value)} />
                    <TextField select size='small' label='Église' value={egliseNum} onChange={e => setEgliseNum(e.target.value)} sx={{minWidth: 220}}>
                        <MenuItem value=''>Toutes</MenuItem>
                        {eglises.sort((a, b) => a.nom < b.nom ? -1 : 1).map(e => <MenuItem key={e._id} value={e.number}>{e.nom} — {e.commune}</MenuItem>)}
                    </TextField>
                    <TextField select size='small' label='Utilisateur' value={userNum} onChange={e => setUserNum(e.target.value)} sx={{minWidth: 180}}>
                        <MenuItem value=''>Tous</MenuItem>
                        {(roleFilter ? users.filter(u => u.role === roleFilter) : users).map(u => <MenuItem key={u.num} value={u.num}>{[u.nom, u.prenom1, u.prenom2].filter(Boolean).join(' ')}</MenuItem>)}
                    </TextField>
                    <TextField select size='small' label="Type d'utilisateur" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setUserNum('') }} sx={{minWidth: 180}}>
                        <MenuItem value=''>Tous</MenuItem>
                        {distinctRoles.map(r => <MenuItem key={r} value={r} sx={{textTransform: 'capitalize'}}>{r}</MenuItem>)}
                    </TextField>
                    <TextField select size='small' label='Action précise' value={actionFilter} onChange={e => setActionFilter(e.target.value)} sx={{minWidth: 220}}>
                        <MenuItem value=''>Toutes</MenuItem>
                        {actions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </TextField>
                    <Button variant='contained' onClick={fetchLogs} disabled={!start_date || !end_date || loading}>
                        {loading ? <CircularProgress size={20} color='inherit'/> : 'Appliquer'}
                    </Button>
                </Stack>

                <Stack direction='row' spacing={1} alignItems='center' mb={2} flexWrap='wrap'>
                    <Typography fontWeight={600} fontSize={14}>Type :</Typography>
                    <ToggleButtonGroup size='small' value={type} onChange={(e, v) => setType(v)}>
                        {Object.entries(TYPE_LABELS).filter(([key]) => key !== 'autre').map(([key, label]) =>
                            <ToggleButton key={key} value={key}>{label}</ToggleButton>
                        )}
                    </ToggleButtonGroup>
                </Stack>

                <TextField size='small' label='Rechercher dans les résultats' value={search_input} disabled={!logs?.length}
                    sx={{width: '50%', minWidth: '280px', mb: 1}}
                    InputProps={{endAdornment: <InputAdornment position='end'><Search/></InputAdornment>}}
                    onChange={e => setSearch(e.target.value)}
                />

                {logs !== null && !loading && (
                    filtered.length === 0
                        ? <Typography color='text.secondary' mt={2}>Aucun log pour cette période et ces filtres.</Typography>
                        : <>
                            <Typography variant='caption' color='text.secondary'>{filtered.length} résultat{filtered.length > 1 ? 's' : ''}{logs.length >= 500 ? ' (limité à 500 — affinez la période)' : ''}</Typography>
                            <SorTable data={filtered} table={table} rowsPerPage={50} rowsPerPageOptions={[25, 50, 100, 200]}/>
                          </>
                )}
            </Box>
            <Backdrop open={loading}><CircularProgress color='inherit'/></Backdrop>
        </div>
    )
}
